import { memo } from "react";
import { LinearGradient } from "expo-linear-gradient";
import { useUnistyles } from "react-native-unistyles";
import { Text } from "../Text";
import { Icon, IconNameType } from "../Icon";

export type AvatarSize = "sm" | "md" | "lg";

export type AvatarProps = {
  name?: string;
  initials?: string;
  icon?: IconNameType;
  size?: AvatarSize;
};

function generateInitialsFromName(name: string): string {
  const nameParts = name.trim().split(/\s+/);
  if (nameParts.length >= 2) {
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

const SIZES: Record<AvatarSize, { container: number; fontSize: "xs/bold" | "md/bold" | "lg/bold" }> = {
  sm: { container: 32, fontSize: "xs/bold" },
  md: { container: 48, fontSize: "md/bold" },
  lg: { container: 64, fontSize: "lg/bold" },
};

export const Avatar = memo(({ name, initials, icon, size = "md" }: AvatarProps) => {
  const { theme } = useUnistyles();
  const { container, fontSize } = SIZES[size];
  const borderRadius = container / 2;
  const iconSize =
    size === "sm" ? 18
    : size === "md" ? 26
    : 32;

  // Generate initials from name if provided, otherwise use provided initials
  const displayInitials = name ? generateInitialsFromName(name) : initials;

  return (
    <LinearGradient
      colors={[theme.colors.navy[900], theme.colors.navy[700]]}
      start={{ x: 1, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{
        width: container,
        height: container,
        borderRadius,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon ?
        <Icon name={icon} size={iconSize} color={theme.colors.white.default} />
      : <Text textStyle={fontSize} style={{ color: theme.colors.white.default }}>
          {displayInitials || "??"}
        </Text>
      }
    </LinearGradient>
  );
});

Avatar.displayName = "Avatar";
